import { db } from '../db';
import { config } from '../config';
import { wsClient } from './WebSocketClient';
import { authenticatedFetch } from './api';

const getCurrentUserEmail = (): string | null => localStorage.getItem(config.USER_EMAIL_KEY);

export interface SyncResult {
    success: boolean;
    count?: number;
    results?: Record<string, any>;
    error?: string;
}

export interface SyncRequestResult {
    success: boolean;
    result?: any;
    skipped?: boolean;
    message?: string;
    error?: string;
}

export interface LogEntry {
    id?: number;
    item: string;
    area: string;
    status: string;
    timestamp: string;
    code: string;
    targetClientEmail?: string | null;
    value: string;
    notes: string;
    remoteId?: string | null;
}

export interface UserEntry {
    id: number;
    email: string;
    role: string;
    sheetUrl?: string;
    token?: string;
    networkStatus?: string;
    billingStatus?: string;
    currentProgram?: string;
    accessTags?: string[];
}

export interface SyncStatus {
    area: string;
    item: string;
    value: string;
    notes: string;
    timestamp: string;
}

export class SyncManager {
    private isSyncing: boolean = false;
    private syncDebounceTimer: number | null = null;

    constructor() {}

    async getPendingCount(): Promise<number> {
        return await db.logs.where('status').equals('pending').count();
    }

    parseValue(valString: string): any {
        try {
            return JSON.parse(valString);
        } catch (e) {
            return { Value: valString };
        }
    }

    async syncNow(): Promise<SyncResult> {
        const email = getCurrentUserEmail();
        if (!email) {
            console.warn("[Sync] No User Identity set. Skipping sync.");
            return { success: false, error: "No Identity" };
        }

        if (this.isSyncing) {return { success: true, count: 0 };}

        return new Promise((resolve) => {
            if (this.syncDebounceTimer) {clearTimeout(this.syncDebounceTimer);}
            this.syncDebounceTimer = window.setTimeout(async () => {
                const result = await this.executeSync();
                resolve(result);
            }, 1000) as any;
        });
    }

    async executeSync(): Promise<SyncResult> {
        if (this.isSyncing) {return { success: true, count: 0 };}

        const email = getCurrentUserEmail();
        if (!email) {return { success: false, error: "No Identity" };}

        try {
            this.isSyncing = true;
            console.log("☁️ Multi-tenant sync started...", email);

            const allPendingLogs = await db.logs.where('status').equals('pending').toArray() as LogEntry[];

            if (allPendingLogs.length === 0) {
                await this.sendSyncRequest(email, []);
                this.isSyncing = false;
                return { success: true, count: 0 };
            }

            const logsByClient = allPendingLogs.reduce((acc: Record<string, LogEntry[]>, log) => {
                const target = log.targetClientEmail || email;
                if (!acc[target]) {acc[target] = [];}
                acc[target].push(log);
                return acc;
            }, {});

            let totalSynced = 0;
            const results: Record<string, any> = {};

            for (const [clientEmail, logs] of Object.entries(logsByClient)) {
                console.log(`📡 Syncing ${logs.length} items for ${clientEmail}...`);
                const result = await this.sendSyncRequest(clientEmail, logs);
                results[clientEmail] = result;
                if (result.success) {totalSynced += logs.length;}
            }

            console.log(`✅ Multi-tenant sync complete. Total items: ${totalSynced}`);
            return { success: true, count: totalSynced, results };

        } catch (error: any) {
            console.error("❌ Sync Manager Error:", error);
            return { success: false, error: error.message };
        } finally {
            this.isSyncing = false;
        }
    }

    async sendSyncRequest(clientEmail: string, logs: LogEntry[]): Promise<SyncRequestResult> {
        if (clientEmail === config.ADMIN_EMAIL) {
            console.log(`[Sync] Skipping client sync for ADMIN user ${clientEmail} - HQ sheet sync handled separately`);
            return { success: true, skipped: true, message: 'Admin user - HQ sync handled separately' };
        }

        const email = getCurrentUserEmail();
        if (!email) {return { success: false, error: "No Identity" };}

        try {
            const isManaged = clientEmail !== email;

            const payload = {
                email: clientEmail,
                managerEmail: email,
                isManaged: isManaged,
                role: clientEmail === config.ADMIN_EMAIL ? 'ADMIN' : 'CLIENT',
                isManualTrigger: true,
                action: 'create',
                changes: logs.map(log => {
                    const parsed = this.parseValue(log.value);
                    return {
                        action: log.remoteId ? 'update' : 'create',
                        rowId: log.remoteId || null,
                        Timestamp: log.timestamp,
                        Item: log.item,
                        Notes: log.notes,
                        SheetTab: log.code,
                        ...parsed
                    };
                })
            };

            const response = await authenticatedFetch(`${config.API_BASE_URL}/api/sync/push`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {throw new Error(`HTTP ${response.status}`);}
            const result = await response.json();

            await (db as any).transaction('rw', db.logs, db.user, async () => {
                for (const log of logs) {
                    if (log.id !== undefined) {
                        await db.logs.update(log.id, { status: 'synced' });
                    }
                }

                if (!isManaged && (result.networkStatus || result.billingStatus || result.currentProgram)) {
                    const currentUser = await db.user.where('email').equals(email).first() as UserEntry | undefined;
                    if (currentUser) {
                        await db.user.update(currentUser.id, {
                            networkStatus: result.networkStatus || currentUser.networkStatus,
                            billingStatus: result.billingStatus || currentUser.billingStatus,
                            accessTags: result.accessTags || currentUser.accessTags,
                            currentProgram: result.currentProgram || currentUser.currentProgram,
                            sheetUrl: result.sheetUrl || currentUser.sheetUrl
                        });
                    }
                }
            });

            const affectedTabs = Array.from(new Set(logs.map(l => l.code)));
            for (const tab of affectedTabs) {
                await this.fetchTabData(tab, clientEmail);
            }

            return { success: true, result };
        } catch (error: any) {
            console.error(`❌ Sync failed for ${clientEmail}:`, error);
            return { success: false, error: error.message };
        }
    }

    async fetchTabData(tabName: string, targetClientEmail: string | null = null, fetchAll: boolean = false): Promise<any> {
        const email = getCurrentUserEmail();
        if (!email) {return null;}

        const normalizedTabName = tabName.replace(/ /g, '_');
        const targetEmail = targetClientEmail || email;
        const emailOnlyTabs = ['ModuleExecutions'];
        const effectiveFetchAll = fetchAll === undefined ? !emailOnlyTabs.includes(tabName) : fetchAll;

        try {
            const isManaged = targetEmail !== email;
            console.log(`📥 Fetching ${tabName} for ${targetEmail} (fetchAll: ${effectiveFetchAll})...`);

            const url = `${config.API_BASE_URL}/api/sync/pull?tabName=${encodeURIComponent(normalizedTabName)}&email=${targetEmail}&managerEmail=${email}&isManaged=${isManaged}${effectiveFetchAll ? '&fetchAll=true' : ''}`;

            const response = await authenticatedFetch(url);
            console.log(`[Sync] Request URL: ${url} | Status: ${response.status}`);
            if (!response.ok) {throw new Error(`Fetch failed: ${response.statusText}`);}

            const text = await response.text();
            console.log(`[Sync] Response text for ${tabName}:`, text.substring(0, 500));
            if (!text || text.trim() === '') {
                console.warn(`⚠️ Empty response for ${tabName}`);
                return null;
            }

            let result;
            try {
                result = JSON.parse(text);
            } catch (parseError) {
                console.error(`❌ JSON Parse Error (${tabName}):`, text.substring(0, 200));
                throw parseError;
            }

            // Ensure we return an array of rows
                        let data: any[] = [];
                        if (Array.isArray(result.data)) {
                            data = result.data;
                        } else if (Array.isArray(result)) {
                            data = result;
                        } else {
                            // fallback to empty array if data is null or not an array
                            data = [];
                        }

            if (!isManaged) {
                await (db as any).transaction('rw', db.tabData, async () => {
                    const existing = await db.tabData.where('tabName').equals(tabName).first();
                    if (existing && existing.id !== undefined) {
                        await db.tabData.update(existing.id, { data, lastSync: new Date().toISOString() });
                    } else {
                        await db.tabData.add({ tabName, data, lastSync: new Date().toISOString() });
                    }
                });
            }

            return data;
        } catch (error: any) {
            console.error(`❌ Fetch Error (${tabName}):`, error);
            const cached = await db.tabData.where('tabName').equals(tabName).first();
            return cached ? cached.data : null;
        }
    }

    async fetchAllSyncStatus(targetEmail: string | null = null): Promise<void> {
        const email = targetEmail || getCurrentUserEmail();
        if (!email) {return;}

        try {
            console.log(`🔄 Hydrating ${email} focus statuses...`);

            const response = await authenticatedFetch(`${config.API_BASE_URL}/api/sync/all?email=${encodeURIComponent(email)}`);

            if (!response.ok) {return;}
            const result = await response.json();
            const data: Record<string, SyncStatus> = result.data || result;

            await (db as any).transaction('rw', db.logs, async () => {
                for (const [code, status] of Object.entries(data)) {
                    const currentEmail = getCurrentUserEmail();
                    const existing = await db.logs.where('code').equals(code)
                        .and((l: any) => (l.targetClientEmail || currentEmail) === email)
                        .reverse().sortBy('timestamp');

                    const latestLocal = existing[0];

                    if (!latestLocal || new Date(status.timestamp) > new Date(latestLocal.timestamp)) {
                        await db.logs.add({
                            area: status.area,
                            item: status.item,
                            code: code,
                            value: status.value,
                            notes: status.notes,
                            timestamp: status.timestamp,
                            status: 'synced',
                            targetClientEmail: email !== currentEmail ? email : null
                        });
                    }
                }
            });
        } catch (error: any) {
            console.error("❌ Sync-All Error:", error);
        }
    }

    async fetchBusinessModules(targetEmail: string | null = null): Promise<void> {
        const email = targetEmail || getCurrentUserEmail();
        if (!email) {return;}

        const businessTabs = [
                    'Project_Milestones',
                    'Billing_Ledger',
                    'Invoices_DB',
                    'Domains_Owned',
                    'GBP_Status',
                    'Master_Products',
                    'Commission_Ledger',
                    'Scripts_Swap_File',
                    'System_Log',
                    'Business_Details',
                    'Customers',
                    'CRM_Leads',
                    'Quotes_Log',
                    'Invoices_Log',
                    'Expenses_Log',
                    'Transactions',
                    'Reconciliation_Log',
                    'Financial_Intelligence',
                    'Budgets',
                    'Communications'
                ];

                console.log(`💼 Fetching Business Module data (concurrent batches)...`);

                // Fetch tabs in concurrent batches of 3 with 500ms delay between batches
        // This reduces total time from 11 * 500ms = 5.5s to ~2s
        const BATCH_SIZE = 3;
        const BATCH_DELAY = 500;
        
        for (let i = 0; i < businessTabs.length; i += BATCH_SIZE) {
            const batch = businessTabs.slice(i, i + BATCH_SIZE);
            await Promise.all(batch.map(tab => this.fetchTabData(tab, email)));
            
            // Delay between batches (not after the last one)
            if (i + BATCH_SIZE < businessTabs.length) {
                await new Promise(r => setTimeout(r, BATCH_DELAY));
            }
        }
    }

    async fetchHistory(): Promise<void> {
        await this.fetchAllSyncStatus();
        return this.fetchBusinessModules();
    }

    async fetchBusinessKPIs(email: string | null = null): Promise<any> {
        const targetEmail = email || getCurrentUserEmail();
        if (!targetEmail) {
            console.warn('[Sync] No user email provided for Business KPIs fetch');
            return null;
        }

        console.log(`📥 Fetching Business_KPIs for ${targetEmail}...`);

        try {
            const cacheKey = `Business_KPIs_${targetEmail}`;
            // Note: we use the BusinessKPIs service for the actual processing
            // and the syncManager for the fetch/cache orchestration.

            // Check if we have recent local data
            const localData = await db.tabData.where('tabName').equals('Business_KPIs').first();
            if (localData && (Date.now() - new Date(localData.lastSync).getTime() < 5 * 60 * 1000)) {
                return localData.data;
            }

            // Fetch from API
            const response = await fetch(`${config.API_BASE_URL}/billing-dashboard-v3?email=${encodeURIComponent(targetEmail)}`);
            if (!response.ok) {throw new Error(`Fetch failed: ${response.status}`);}

            const data = await response.json();

            // Store in local DB
            await db.tabData.put({
                tabName: 'Business_KPIs',
                data: data,
                lastSync: new Date().toISOString()
            });

            return data;
        } catch (error) {
            console.error('❌ Error fetching Business KPIs:', error);
            const cached = await db.tabData.where('tabName').equals('Business_KPIs').first();
            return cached ? cached.data : null;
        }
    }

    async initWebSocket(): Promise<void> {
        wsClient.connect().catch((error: unknown) => {
            console.warn('WebSocket connection error (expected in dev):', error);
        });

        wsClient.on('DASHBOARD_UPDATE', (payload: unknown) => {
            console.log('📡 [WS] Dashboard update received:', payload);
            db.tabData.put({
                tabName: 'Business_KPIs',
                data: payload,
                lastSync: new Date().toISOString()
            });
        });

        wsClient.on('error', (error: unknown) => {
            console.warn('WebSocket error (expected in dev):', error);
        });
    }

    async logAction(tabName: string, action: string, data: any, rowId: string | null = null): Promise<SyncRequestResult> {
        console.log(`📝 Log Action: ${action} on ${tabName}`, data);
        const log: LogEntry = {
            value: JSON.stringify(data),
            timestamp: new Date().toISOString(),
            item: data.Key || 'Update',
            notes: data.Description || '',
            code: tabName,
            remoteId: rowId,
            area: '',
            status: 'pending'
        };
        return this.sendSyncRequest(getCurrentUserEmail() || '', [log]);
    }

    async triggerWorkflow(webhookPath: string, payload: any): Promise<any> {
        const email = getCurrentUserEmail();
        if (!email) {return null;}
        try {
            console.log(`🚀 Triggering Workflow: ${webhookPath}...`);
            const response = await fetch(`${config.API_BASE_URL}/webhook/${webhookPath}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: email,
                    ...payload
                })
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(`Trigger failed (${response.status}): ${text || response.statusText}`);
            }
            return await response.json();
        } catch (error: any) {
            console.error(`❌ Workflow Trigger Error (${webhookPath}):`, error);
            throw error;
        }
    }

    async approveWorkflow(workflowId: number, payload: any): Promise<SyncRequestResult> {
        console.log(`✅ Approving Workflow ${workflowId}...`);
        try {
            await db.pendingWorkflows.update(workflowId, { status: 'approved' });

            const email = getCurrentUserEmail();
            const response = await authenticatedFetch(`${config.API_BASE_URL}/api/workflows/approve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    workflowId,
                    payload,
                    managerEmail: email
                })
            });

            if (!response.ok) throw new Error(`Approval API failed: ${response.status}`);
            return { success: true };
        } catch (error: any) {
            console.error(`❌ approveWorkflow error:`, error);
            return { success: false, error: error.message };
        }
    }

    async completeManualWorkflow(workflowId: number, notes: string): Promise<SyncRequestResult> {
        console.log(`⚡ Completing Manual Workflow ${workflowId}...`);
        try {
            await db.pendingWorkflows.update(workflowId, { status: 'completed', notes });

            const email = getCurrentUserEmail();
            const response = await authenticatedFetch(`${config.API_BASE_URL}/api/workflows/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    workflowId,
                    notes,
                    managerEmail: email
                })
            });

            if (!response.ok) throw new Error(`Completion API failed: ${response.status}`);
            return { success: true };
        } catch (error: any) {
            console.error(`❌ completeManualWorkflow error:`, error);
            return { success: false, error: error.message };
        }
    }

    async convertQuoteToInvoice(invoiceId: string): Promise<any> {
        return this.triggerWorkflow('hq-accept-quote', { invoiceId });
    }

    async updateTabData(tabName: string, rowData: Record<string, unknown>): Promise<SyncRequestResult> {
        console.log(`✏️ Update Tab Data: ${tabName}`, rowData);
        const email = getCurrentUserEmail();
        if (!email) {
            return { success: false, error: "No Identity" };
        }

        try {
            const normalizedTabName = tabName.replace(/ /g, '_');
            const payload = {
                email: email,
                action: 'create',
                changes: [{
                    action: 'create',
                    rowId: null,
                    Timestamp: new Date().toISOString(),
                    ...rowData
                }]
            };

            const response = await authenticatedFetch(`${config.API_BASE_URL}/api/sync/push?tabName=${encodeURIComponent(normalizedTabName)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const result = await response.json();
            console.log(`✅ Tab Data Updated: ${tabName}`, result);
            return { success: true, result };
        } catch (error: any) {
            console.error(`❌ Update Tab Data Error (${tabName}):`, error);
            return { success: false, error: error.message };
        }
    }
}

export const syncManager = new SyncManager();
